import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import FilterCheckbox from '@/common/components/FilterCheckbox'
import KanbanTable from '@/common/components/KanbanTable'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { UserData } from '@/common/types/users'
import { CreateModal } from '@/features/establishmentLeadsFunnel/components/CreateModal'
import { columnStateMapping } from '@/features/establishmentLeadsFunnel/constants/columnStateMapping.constant'
import { stateOptions } from '@/features/establishmentLeadsFunnel/constants/stateOptions.constant'
import { useEstablishmentLeadsInfinite } from '@/features/establishmentLeadsFunnel/hooks/useEstablishmentLeadsInfinite'
import { EstablishmentLeadData } from '@/features/establishmentLeadsFunnel/types/establishmentLead.type'
import { FuelIcon, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEstablishmentLeadRelatedUsers } from '../../hooks/useEstablishmentLeadRelatedUsers'

const List = () => {
  const navigate = useNavigate()

  const { filterParams, setFilterParams } = useFilterParams()

  const [searchLeadName, setSearchLeadName] = useDebounce(filterParams.leadName || '')
  const [searchCreator, setSearchCreator] = useDebounce('')

  const {
    data: registeredData = [],
    isPending: isPendingRegistered,
    hasNextPage: hasNextPageRegistered,
    isFetchingNextPage: isFetchingNextPageRegistered,
    fetchNextPage: fetchNextPageRegistered,
    error: registeredError = {} as any,
    refetch: refetchRegistered,
  } = useEstablishmentLeadsInfinite<EstablishmentLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.registered,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
  })

  const {
    data: qualifyingData = [],
    isPending: isPendingQualifying,
    hasNextPage: hasNextPageQualifying,
    isFetchingNextPage: isFetchingNextPageQualifying,
    fetchNextPage: fetchNextPageQualifying,
    refetch: refetchQualifying,
  } = useEstablishmentLeadsInfinite<EstablishmentLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.qualifying,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
  })

  const {
    data: deployingData = [],
    isPending: isPendingDeploying,
    hasNextPage: hasNextPageDeploying,
    isFetchingNextPage: isFetchingNextPageDeploying,
    fetchNextPage: fetchNextPageDeploying,
    refetch: refetchDeploying,
  } = useEstablishmentLeadsInfinite<EstablishmentLeadData[]>({
    filters: {
      search: searchLeadName || '',
      state: filterParams.state?.value ? [filterParams.state.value] : columnStateMapping.deploying,
      creatorId: filterParams.creator?.value || '',
      showOnlyArchived: filterParams.archived === 'true',
    },
  })

  const { data: users } = useEstablishmentLeadRelatedUsers({
    filters: {
      search: searchCreator,
    },
    mapper: Mapper.mapToOptions<UserData>({ labelFieldName: 'full_name', valueFieldName: 'id' }),
  })

  const refetchAll = () => {
    refetchRegistered()
    refetchQualifying()
    refetchDeploying()
  }

  return (
    <div className="flex flex-col gap-y-6">
      <ListHeader Icon={<FuelIcon className="size-10" strokeWidth={1.5} />} title="Funil de Credenciados" />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={registeredError?.status}
        refetchFunction={refetchAll}
      >
        <TextField
          LeftIcon={<Search />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          value={filterParams.leadName || ''}
          onChange={(e: any) => {
            setSearchLeadName(e.target?.value)
            setFilterParams((prev) => ({
              ...prev,
              leadName: e.target?.value,
            }))
          }}
        />
        <div className="flex justify-between">
          <ListFilters
            filters={[
              {
                className: 'min-w-40',
                onChange: (e: any) => {
                  setFilterParams((prev) => ({
                    ...prev,
                    state: e.value ? e : undefined,
                  }))
                },
                value: filterParams.state || undefined,
                options: [
                  { label: 'Todos', value: '' },
                  ...stateOptions.filter((option) => option.value !== 'completed'),
                ],
                placeholder: 'Estado',
                dataTestid: 'state_filter',
              },
              {
                className: '',
                onInputChange: (inputValue) => setSearchCreator(inputValue),
                onChange: (e: any) => {
                  setFilterParams((prev) => ({
                    ...prev,
                    creator: e.value ? e : undefined,
                  }))
                },
                value: filterParams.creator || undefined,
                options: [{ label: 'Todos', value: '' }, ...(users || [])],
                placeholder: 'Credenciador',
              },
            ]}
          />

          <FilterCheckbox
            isChecked={filterParams.archived === 'true'}
            onChange={(value: any) => {
              setFilterParams((prev) => ({
                ...prev,
                archived: value,
              }))
            }}
            label="Exibir apenas Arquivados"
            id="filter-checkbox"
            name="filter-checkbox"
          />
        </div>
        <div>
          <KanbanTable
            columns={[
              {
                title: 'Registrados',
                cards:
                  !filterParams.state?.value || columnStateMapping.registered.includes(filterParams.state?.value)
                    ? registeredData
                    : [],
                headerButton: <CreateModal />,
                onClickOnCard: (id) => navigate(`/establishment-leads/${id}/edit`),
                isPending: isPendingRegistered,
                hasNextPage: hasNextPageRegistered,
                isFetchingNextPage: isFetchingNextPageRegistered,
                fetchNextPage: fetchNextPageRegistered,
                skeletonCount: 2,
                dataTestid: 'registered_column',
              },
              {
                title: 'Qualificando',
                cards:
                  !filterParams.state?.value || columnStateMapping.qualifying.includes(filterParams.state?.value)
                    ? qualifyingData
                    : [],
                onClickOnCard: (id) => navigate(`/establishment-leads/${id}/edit`),
                isPending: isPendingQualifying,
                hasNextPage: hasNextPageQualifying,
                isFetchingNextPage: isFetchingNextPageQualifying,
                fetchNextPage: fetchNextPageQualifying,
                skeletonCount: 4,
                dataTestid: 'qualifying_column',
              },
              {
                title: 'Implantação',
                cards:
                  !filterParams.state?.value || columnStateMapping.deploying.includes(filterParams.state?.value)
                    ? deployingData
                    : [],
                onClickOnCard: (id) => navigate(`/establishment-leads/${id}/edit`),
                isPending: isPendingDeploying,
                hasNextPage: hasNextPageDeploying,
                isFetchingNextPage: isFetchingNextPageDeploying,
                fetchNextPage: fetchNextPageDeploying,
                dataTestid: 'deploying_column',
              },
            ]}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
